import {
    Box,
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
} from '@mui/material';

export const SUBSCRIPTION_REQUIRED_TITLE = 'Select a Plan';
export const SUBSCRIPTION_REQUIRED_MESSAGE = 'You must select a subscription plan to continue using AttorneyAide.';

export function hasActiveSubscription(subscriptionStatus) {
    return subscriptionStatus === 'active' || subscriptionStatus === 'trialing';
}

/** Full product access: Stripe active/trialing, or billing.subscriptionExempt. */
export function hasProductAccess(user) {
    if (user?.subscriptionExempt === true) return true;
    return hasActiveSubscription(user?.subscriptionStatus);
}

const ctaButtonSx = {
    background: 'var(--button-gradient)',
    color: '#fff',
    fontWeight: 600,
    textTransform: 'none',
    fontSize: '0.95rem',
    padding: '12px 28px',
    borderRadius: '12px',
    border: 'none',
    boxShadow: '0 4px 15px rgba(212, 175, 55, 0.4)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
    overflow: 'hidden',
    '&:hover': {
        transform: 'translateY(-3px)',
        boxShadow: '0 8px 25px rgba(212, 175, 55, 0.5)',
        background: 'var(--button-gradient-hover)',
    },
    '&:active': {
        transform: 'translateY(-1px)',
        boxShadow: '0 4px 15px rgba(212, 175, 55, 0.4)',
    },
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: '-100%',
        width: '100%',
        height: '100%',
        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
        transition: 'left 0.6s',
    },
    '&:hover::before': {
        left: '100%',
    },
};

const cancelButtonSx = {
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '0.9rem',
    padding: '10px 20px',
    borderRadius: '12px',
    border: '1px solid var(--border-color)',
    color: 'var(--text-primary)',
    backgroundColor: 'transparent',
    '&:hover': {
        background: 'var(--surface-color)',
        borderColor: 'var(--text-secondary)',
    },
};

const titleSx = {
    color: 'var(--text-primary)',
    fontWeight: 500,
    fontSize: '1.35rem',
    letterSpacing: '-0.01em',
    lineHeight: 1.3,
};

const messageSx = {
    color: 'var(--text-secondary)',
    fontSize: '1rem',
    lineHeight: 1.7,
    fontWeight: 400,
};

/**
 * Shared copy and actions for the subscription-required prompt.
 * Use variant="dialog" inside a MUI Dialog, or variant="inline" for a page panel.
 */
export default function SubscriptionRequiredContent({
    onChoosePlan,
    onCancel,
    showCancel = true,
    variant = 'inline',
}) {
    const cancelButton = showCancel && onCancel ? (
        <Button onClick={onCancel} sx={cancelButtonSx}>
            Cancel
        </Button>
    ) : null;

    const choosePlanButton = (
        <Button onClick={onChoosePlan} sx={ctaButtonSx} autoFocus disableElevation>
            Select Now
        </Button>
    );

    if (variant === 'dialog') {
        return (
            <>
                <DialogTitle
                    id="subscription-required-title"
                    sx={{
                        ...titleSx,
                        padding: '28px 28px 8px',
                    }}
                >
                    {SUBSCRIPTION_REQUIRED_TITLE}
                </DialogTitle>
                <DialogContent sx={{ padding: '8px 28px 8px' }}>
                    <Typography id="subscription-required-description" sx={messageSx}>
                        {SUBSCRIPTION_REQUIRED_MESSAGE}
                    </Typography>
                </DialogContent>
                <DialogActions
                    sx={{
                        padding: '20px 28px 28px',
                        gap: 1.5,
                        justifyContent: 'flex-end',
                    }}
                >
                    {cancelButton}
                    {choosePlanButton}
                </DialogActions>
            </>
        );
    }

    return (
        <Box
            sx={{
                width: '100%',
                maxWidth: '440px',
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
            }}
        >
            <Typography
                id="subscription-required-title"
                component="h2"
                sx={{
                    ...titleSx,
                    marginBottom: '12px',
                }}
            >
                {SUBSCRIPTION_REQUIRED_TITLE}
            </Typography>
            <Typography
                id="subscription-required-description"
                sx={{
                    ...messageSx,
                    marginBottom: '28px',
                    maxWidth: '380px',
                }}
            >
                {SUBSCRIPTION_REQUIRED_MESSAGE}
            </Typography>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1.5,
                }}
            >
                {cancelButton}
                {choosePlanButton}
            </Box>
        </Box>
    );
}
